import { useState, type FormEvent } from "react";
import { Copy, ExternalLink, Mail } from "lucide-react";
import {
  AuthError,
  requestEmailCode,
  signInWithApple,
  signInWithGoogle,
  verifyEmailCode,
  type SignInSurface,
} from "../lib/auth";
import { useFlag } from "../lib/flags";
import { buildSystemBrowserHandoffUrl } from "../lib/handoff";
import {
  IN_APP_HOST_LABEL,
  handOffToSystemBrowser,
  inAppBrowserHost,
  systemBrowserName,
} from "../lib/inAppBrowser";
import { isRender3dOnboardingActive } from "../lib/render3dOnboarding";
import { track } from "../lib/telemetry";
import { cn } from "../lib/cn";
import { GoogleGlyph } from "./ui/GoogleGlyph";

/**
 * The provider buttons every sign-in surface shares: Google, Apple (behind
 * `apple_sign_in`) and a six-digit email code (behind `email_otp`). Inside
 * an in-app browser, where Google and Apple can't run, those two buttons
 * hand the visitor to Safari / Chrome with their room and campaign
 * attribution in the URL; the email code works in place.
 *
 * An anonymous session is *linked* by all three paths, so the room and
 * free layouts made before signing up stay with the account.
 */
export function AuthProviders({
  surface,
  onSignedIn,
  resume = "save",
}: {
  surface: SignInSurface;
  onSignedIn?: (info: { method: "google" | "apple" | "email" }) => void;
  /** What the system browser should do on arrival after a handoff. */
  resume?: "save";
}) {
  const appleOn = useFlag("apple_sign_in");
  const emailOn = useFlag("email_otp");
  const host = inAppBrowserHost();
  const browser = systemBrowserName();
  const [busy, setBusy] = useState<"google" | "apple" | "email" | "handoff" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  async function oauth(method: "google" | "apple") {
    setError(null);
    setNotice(null);
    if (host) {
      setBusy("handoff");
      if (isRender3dOnboardingActive()) track("render_3d_handoff_clicked", { host, reason: "sign_in" });
      else track("feng_shui_handoff_clicked", { host });
      // The URL names whichever first run is active, so Safari picks it up.
      const url = await buildSystemBrowserHandoffUrl({ resume });
      const result = await handOffToSystemBrowser(url);
      setBusy(null);
      if (result === "copied") setNotice(`Link copied. Paste it in ${browser} to finish signing in.`);
      else if (result === "failed") setNotice(`Open this page in ${browser} to finish signing in.`);
      return;
    }
    setBusy(method);
    try {
      await (method === "google" ? signInWithGoogle(surface) : signInWithApple(surface));
      onSignedIn?.({ method });
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.kind === "redirecting") return; // page is leaving
        if (err.kind !== "cancelled") setError(err.message);
      } else {
        setError((err as Error).message || "Sign-in failed.");
      }
    } finally {
      setBusy(null);
    }
  }

  const providerButton =
    "h-12 w-full inline-flex items-center justify-center gap-3 rounded-xl bg-white text-ink-900 ring-1 ring-ink-300 hover:bg-ink-50 active:bg-ink-100 transition-colors font-medium disabled:opacity-60 disabled:pointer-events-none";

  return (
    <div className="flex flex-col gap-2" data-testid="auth-providers">
      <button
        type="button"
        onClick={() => void oauth("google")}
        disabled={busy !== null}
        className={providerButton}
        data-testid="auth-google"
      >
        {host ? <ExternalLink className="size-4 text-ink-500" aria-hidden /> : <GoogleGlyph className="size-5" />}
        {busy === "google" ? "Signing in…" : host ? `Google · opens ${browser}` : "Continue with Google"}
      </button>
      {appleOn && (
        <button
          type="button"
          onClick={() => void oauth("apple")}
          disabled={busy !== null}
          className={cn(providerButton, "bg-ink-900 text-white ring-ink-900 hover:bg-ink-800 active:bg-ink-900")}
          data-testid="auth-apple"
        >
          {host ? <ExternalLink className="size-4" aria-hidden /> : <AppleGlyph className="size-5" />}
          {busy === "apple" ? "Signing in…" : host ? `Apple · opens ${browser}` : "Continue with Apple"}
        </button>
      )}
      {emailOn &&
        (emailOpen ? (
          <EmailCodeForm
            surface={surface}
            onSignedIn={() => onSignedIn?.({ method: "email" })}
            onBusy={(b) => setBusy(b ? "email" : null)}
            disabled={busy !== null && busy !== "email"}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setEmailOpen(true);
            }}
            disabled={busy !== null}
            className={providerButton}
            data-testid="auth-email"
          >
            <Mail className="size-5 text-ink-600" aria-hidden />
            Email me a code
          </button>
        ))}
      {host && (
        <p className="text-caption leading-snug text-ink-500">
          Google{appleOn ? " and Apple" : ""} can&apos;t sign you in inside {IN_APP_HOST_LABEL[host]};
          we&apos;ll open {browser} with your room.{emailOn ? " The email code works right here." : ""}
        </p>
      )}
      {notice && (
        <div role="status" className="flex items-start gap-2 rounded-xl bg-ink-50 px-3 py-2 text-sm text-ink-700 ring-1 ring-ink-200">
          <Copy className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden />
          {notice}
        </div>
      )}
      {error && (
        <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">
          {error}
        </div>
      )}
    </div>
  );
}

function EmailCodeForm({
  surface,
  onSignedIn,
  onBusy,
  disabled,
}: {
  surface: SignInSurface;
  onSignedIn: () => void;
  onBusy: (busy: boolean) => void;
  disabled: boolean;
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function run(fn: () => Promise<void>) {
    setBusy(true);
    onBusy(true);
    setError(null);
    void fn()
      .catch((err) => {
        if (err instanceof AuthError && err.kind === "cancelled") return;
        setError(err instanceof Error ? err.message : "That didn't work. Try again.");
      })
      .finally(() => {
        setBusy(false);
        onBusy(false);
      });
  }

  function sendCode(e: FormEvent) {
    e.preventDefault();
    run(async () => {
      await requestEmailCode(email.trim());
      setStage("code");
    });
  }

  function verify(e: FormEvent) {
    e.preventDefault();
    run(async () => {
      await verifyEmailCode(surface, email.trim(), code);
      onSignedIn();
    });
  }

  const input =
    "h-12 w-full rounded-xl bg-white px-3 text-base text-ink-900 ring-1 ring-ink-300 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-900";

  return (
    <form
      onSubmit={stage === "email" ? sendCode : verify}
      className="flex flex-col gap-2 rounded-2xl bg-ink-50 p-2.5 ring-1 ring-ink-200"
      data-testid="auth-email-form"
    >
      {stage === "email" ? (
        <>
          <label className="sr-only" htmlFor="auth-email-input">
            Email address
          </label>
          <input
            id="auth-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={input}
          />
          <button
            type="submit"
            disabled={busy || disabled || email.trim().length < 5}
            className="h-11 w-full rounded-xl bg-ink-900 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="auth-email-send"
          >
            {busy ? "Sending…" : "Send code"}
          </button>
        </>
      ) : (
        <>
          <p className="px-1 text-caption text-ink-600">
            We emailed a 6-digit code to <span className="font-semibold text-ink-900">{email}</span>.
          </p>
          <label className="sr-only" htmlFor="auth-code-input">
            Six-digit code
          </label>
          <input
            id="auth-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className={cn(input, "text-center text-xl tracking-[0.4em]")}
          />
          <button
            type="submit"
            disabled={busy || disabled || code.length !== 6}
            className="h-11 w-full rounded-xl bg-ink-900 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="auth-email-verify"
          >
            {busy ? "Checking…" : "Continue"}
          </button>
          <button
            type="button"
            onClick={() => {
              setStage("email");
              setCode("");
            }}
            className="h-9 text-caption font-medium text-ink-500 hover:text-ink-900"
          >
            Use a different email
          </button>
        </>
      )}
      {error && (
        <div role="alert" className="rounded-lg bg-rose-50 px-2.5 py-1.5 text-caption text-rose-700 ring-1 ring-rose-200">
          {error}
        </div>
      )}
    </form>
  );
}

function AppleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
      <path d="M16.37 12.76c-.03-2.55 2.08-3.77 2.17-3.83-1.18-1.73-3.02-1.97-3.68-2-1.57-.16-3.06.92-3.86.92-.8 0-2.02-.9-3.33-.87-1.71.03-3.29 1-4.17 2.53-1.78 3.09-.46 7.66 1.28 10.17.85 1.23 1.86 2.6 3.18 2.55 1.28-.05 1.76-.83 3.3-.83 1.54 0 1.98.83 3.33.8 1.38-.02 2.25-1.25 3.09-2.48.97-1.42 1.37-2.8 1.4-2.87-.03-.01-2.68-1.03-2.71-4.09zM13.84 5.27c.7-.86 1.18-2.05 1.05-3.24-1.01.04-2.24.68-2.97 1.53-.65.75-1.22 1.96-1.07 3.12 1.13.09 2.28-.57 2.99-1.41z" />
    </svg>
  );
}
